import { useState, useEffect, useCallback } from 'react';
import {
  Box, Typography, Paper, Button, Chip, Alert, CircularProgress,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  TextField, Divider, Dialog, DialogTitle, DialogContent, DialogActions,
  Radio, Tooltip, IconButton, Select, MenuItem,
} from '@mui/material';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import PrintIcon from '@mui/icons-material/Print';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import RefreshIcon from '@mui/icons-material/Refresh';
import TrackChangesIcon from '@mui/icons-material/TrackChanges';
import SearchIcon from '@mui/icons-material/Search';
import ScaleIcon from '@mui/icons-material/Scale';
import HistoryIcon from '@mui/icons-material/History';
import BookmarkIcon from '@mui/icons-material/Bookmark';
import BookmarkAddIcon from '@mui/icons-material/BookmarkAdd';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import EditIcon from '@mui/icons-material/Edit';
import BlockIcon from '@mui/icons-material/Block';
import { useSelector } from 'react-redux';
import { api } from '../../api/api';
import toast from 'react-hot-toast';

const DEFAULT_PARCEL = { length: 13, width: 10, height: 2, weight: 24 }; // weight in oz

const ADDRESS_BOOK_LS = 'digntecShippingAddressBook';
const SELECTED_FROM_LS = 'digntecShippingSelectedFrom';

const DEFAULT_ADDRESSES = [
  { id: 'default-hq', label: 'Digntec HQ (Edgewater)', company: 'Digntec LLC', street1: '15 Veterans Way', city: 'Edgewater', state: 'NJ', zip: '07020', phone: '2013132655' },
  { id: 'default-elmwood', label: 'Digntec (Elmwood Park)', company: 'Digntec LLC', street1: '37 Midland Ave STE A3', city: 'Elmwood Park', state: 'NJ', zip: '07407', phone: '2013577222' },
];

function loadAddressBook() {
  try {
    const raw = localStorage.getItem(ADDRESS_BOOK_LS);
    if (!raw) return [...DEFAULT_ADDRESSES];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) && parsed.length > 0 ? parsed : [...DEFAULT_ADDRESSES];
  } catch { return [...DEFAULT_ADDRESSES]; }
}

function saveAddressBook(book) {
  try { localStorage.setItem(ADDRESS_BOOK_LS, JSON.stringify(book)); } catch (_) {}
}

function loadSelectedFromId() {
  try { return localStorage.getItem(SELECTED_FROM_LS) || DEFAULT_ADDRESSES[0].id; }
  catch { return DEFAULT_ADDRESSES[0].id; }
}

// 하위 호환: 기존 단일 shipFrom 로드
const SHIP_FROM_LS = 'digntecShippingFromV1';
function loadShipFrom() {
  try {
    const raw = localStorage.getItem(SHIP_FROM_LS);
    if (!raw) return null;
    return JSON.parse(raw);
  } catch { return null; }
}

/** "505-917-5066, 505-123-4567" → ["505-917-5066", "505-123-4567"] (쉼표/줄바꿈/세미콜론/슬래시 구분) */
function splitPhones(raw) {
  return String(raw || '').split(/[,\n;/|]+/).map(s => s.trim()).filter(Boolean);
}
/** 전화번호 여러 개를 줄바꿈으로 이어 붙여 입력 필드에 두 줄로 표시 */
function formatPhoneLines(raw) {
  return splitPhones(raw).join('\n');
}

function formatAddressTooltip(s) {
  const line1 = s.address1 || '';
  const line2 = [s.town, s.state, s.zipcode].filter(Boolean).join(', ');
  return [line1, line2].filter(Boolean).join('\n') || '주소 없음';
}

export default function EasyPostSettings() {
  const { user } = useSelector(s => s.User);
  const [stores, setStores] = useState([]);
  const [loadingStores, setLoadingStores] = useState(false);
  const [storeSearch, setStoreSearch] = useState('');
  const [shippingFilter, setShippingFilter] = useState('all'); // all | sent
  const [shippingSort, setShippingSort] = useState('default'); // default | recent-sent

  // 라벨 생성 플로우
  const [selectedStore, setSelectedStore] = useState(null);
  const [rates, setRates] = useState([]);
  const [selectedRate, setSelectedRate] = useState(null);
  const [loadingRates, setLoadingRates] = useState(false);
  const [creatingLabel, setCreatingLabel] = useState(false);
  const [labelResult, setLabelResult] = useState(null);
  const [labelDialogOpen, setLabelDialogOpen] = useState(false);
  const [withReturn, setWithReturn] = useState(false);
  // 리턴 라벨만 생성 (아웃바운드 없이) — 매장에서 우리 쪽으로 보내는 반품용
  const [returnOnly, setReturnOnly] = useState(false);
  // 리턴 라벨 반송 수신지 — '' 이면 발송지(FROM)와 동일, 아니면 주소록 id
  const [returnToId, setReturnToId] = useState('');
  // 리턴 배송 방법 — 아웃바운드(급함)와 별도로 느리고 싼 방법 선택 가능. 기본 최저가
  const [returnRates, setReturnRates] = useState([]);
  const [selectedReturnRate, setSelectedReturnRate] = useState(null);
  const [loadingReturnRates, setLoadingReturnRates] = useState(false);
  // 수취인 주소 확인 결과
  const [addrCheck, setAddrCheck] = useState(null);
  const [checkingAddr, setCheckingAddr] = useState(false);

  // 패키지 설정
  const [parcelWeight, setParcelWeight] = useState(DEFAULT_PARCEL.weight);
  const [parcelLength, setParcelLength] = useState(DEFAULT_PARCEL.length);
  const [parcelWidth, setParcelWidth] = useState(DEFAULT_PARCEL.width);
  const [parcelHeight, setParcelHeight] = useState(DEFAULT_PARCEL.height);

  // 수취인 정보 (수정 가능)
  const [toName, setToName] = useState('');
  const [toStreet, setToStreet] = useState('');
  const [toCity, setToCity] = useState('');
  const [toState, setToState] = useState('');
  const [toZip, setToZip] = useState('');
  const [toPhone, setToPhone] = useState('');

  /** 발송지 주소록 */
  const [addressBook, setAddressBook] = useState(() => {
    const book = loadAddressBook();
    // 기존 단일 shipFrom이 있으면 주소록에 마이그레이션
    const legacy = loadShipFrom();
    if (legacy && legacy.street1 && !book.find(a => a.street1 === legacy.street1 && a.zip === legacy.zip)) {
      const migrated = { id: 'migrated-' + Date.now(), label: `${legacy.company || 'Custom'} (${legacy.city || ''})`, ...legacy };
      book.push(migrated);
      saveAddressBook(book);
    }
    return book;
  });
  const [selectedFromId, setSelectedFromId] = useState(loadSelectedFromId);
  const [addressDialogOpen, setAddressDialogOpen] = useState(false);
  const [editingAddress, setEditingAddress] = useState(null); // null=신규, object=수정

  const shipFrom = addressBook.find(a => a.id === selectedFromId) || addressBook[0] || DEFAULT_ADDRESSES[0];

  const selectFromAddress = (id) => {
    setSelectedFromId(id);
    try { localStorage.setItem(SELECTED_FROM_LS, id); } catch (_) {}
  };

  const saveAddress = (addr) => {
    let updated;
    if (addr.id && addressBook.find(a => a.id === addr.id)) {
      updated = addressBook.map(a => a.id === addr.id ? addr : a);
    } else {
      addr.id = 'addr-' + Date.now();
      updated = [...addressBook, addr];
    }
    setAddressBook(updated);
    saveAddressBook(updated);
    selectFromAddress(addr.id);
    setAddressDialogOpen(false);
    setEditingAddress(null);
    toast.success('주소록 저장됨');
  };

  const deleteAddress = (id) => {
    if (addressBook.length <= 1) { toast.error('최소 1개 주소가 필요합니다'); return; }
    const updated = addressBook.filter(a => a.id !== id);
    setAddressBook(updated);
    saveAddressBook(updated);
    if (selectedFromId === id) selectFromAddress(updated[0]?.id);
    toast.success('주소 삭제됨');
  };

  // 트래킹
  const [trackingInfo, setTrackingInfo] = useState(null);

  // 라벨 이력
  const [labelHistory, setLabelHistory] = useState([]);
  const [historyDialogOpen, setHistoryDialogOpen] = useState(false);
  const [historyStore, setHistoryStore] = useState(null);

  useEffect(() => { if (user?._id || user?.user?._id) fetchStores(); }, [user]);

  const fromQueryParams = useCallback(() => ({
    fromCompany: shipFrom.company,
    fromStreet1: shipFrom.street1,
    fromCity: shipFrom.city,
    fromState: shipFrom.state,
    fromZip: shipFrom.zip,
    fromPhone: shipFrom.phone,
  }), [shipFrom]);

  const fetchStores = async () => {
    setLoadingStores(true);
    try {
      const userId = user?._id || user?.user?._id || '';
      const { data } = await api.get(`/store/getall/${userId}`);
      const list = data?.stores || data || [];
      setStores(Array.isArray(list) ? list : []);
    } catch (_) { setStores([]); }
    finally { setLoadingStores(false); }
  };

  const openLabelDialog = async (store) => {
    setSelectedStore(store);
    setRates([]);
    setSelectedRate(null);
    setLabelResult(null);
    setWithReturn(false);
    setReturnOnly(false);
    setReturnToId('');
    setReturnRates([]);
    setSelectedReturnRate(null);
    setAddrCheck(null);
    // 수취인 정보 자동 채움
    setToName(store.ownerName || store.name || '');
    setToStreet(store.address1 || '');
    setToCity(store.town || '');
    setToState(store.state || '');
    setToZip(store.zipcode || '');
    setToPhone(formatPhoneLines(store.ownerPhone || store.storePhone || ''));
    setLabelDialogOpen(true);
    fetchLabelHistory(store._id);
  };

  const fetchLabelHistory = async (storeId) => {
    try {
      const { data } = await api.get(`/store/shipping-labels/${storeId}`);
      setLabelHistory(data.labels || []);
    } catch (_) { setLabelHistory([]); }
  };

  const voidLabel = async (labelId, storeId) => {
    if (!window.confirm('이 라벨을 취소(Void)하시겠습니까?\nVoid this label? Refund will be processed.')) return;
    try {
      await api.post(`/store/shipping-void/${labelId}`);
      toast.success('라벨 취소 완료 / Label voided');
      fetchStores();
      if (storeId) fetchLabelHistory(storeId);
    } catch (e) {
      toast.error(e.response?.data?.message || 'Void failed / 취소 실패');
    }
  };

  const openHistoryDialog = async (store) => {
    setHistoryStore(store);
    setHistoryDialogOpen(true);
    try {
      const { data } = await api.get(`/store/shipping-labels/${store._id}`);
      setLabelHistory(data.labels || []);
    } catch (_) { setLabelHistory([]); }
  };

  const fetchRates = async () => {
    if (!selectedStore) return;
    setLoadingRates(true);
    setRates([]);
    setSelectedRate(null);
    try {
      const { data } = await api.get(`/store/shipping-rates/${selectedStore._id}`, {
        params: {
          weight: parcelWeight,
          length: parcelLength,
          width: parcelWidth,
          height: parcelHeight,
          toName, toStreet, toCity, toState, toZip, toPhone,
          ...fromQueryParams(),
        },
      });
      setRates(data.rates || []);
      if (data.rates?.length > 0) setSelectedRate(0);
    } catch (e) {
      toast.error('요금 조회 실패: ' + (e.response?.data?.message || e.message));
    }
    setLoadingRates(false);
  };

  /** 리턴 요금 조회 (매장 → 반송 수신지). 아웃바운드와 다른 배송 방법을 고를 수 있게 별도 조회 */
  const fetchReturnRates = async () => {
    if (!selectedStore) return;
    setLoadingReturnRates(true);
    setReturnRates([]);
    setSelectedReturnRate(null);
    try {
      const returnTo = returnToId ? addressBook.find(a => a.id === returnToId) : null;
      const { data } = await api.get(`/store/shipping-return-rates/${selectedStore._id}`, {
        params: {
          weight: parcelWeight, length: parcelLength, width: parcelWidth, height: parcelHeight,
          toName, toStreet, toCity, toState, toZip, toPhone,
          ...fromQueryParams(),
          ...(returnTo ? {
            returnCompany: returnTo.company, returnStreet1: returnTo.street1, returnCity: returnTo.city,
            returnState: returnTo.state, returnZip: returnTo.zip, returnPhone: returnTo.phone,
          } : {}),
        },
      });
      setReturnRates(data.rates || []);
      if (data.rates?.length > 0) setSelectedReturnRate(0); // 기본: 최저가
    } catch (e) {
      toast.error('리턴 요금 조회 실패: ' + (e.response?.data?.message || e.message));
    }
    setLoadingReturnRates(false);
  };

  // 리턴 체크 또는 반송 수신지 변경 시 리턴 요금 재조회
  useEffect(() => {
    if ((withReturn || returnOnly) && selectedStore && !labelResult) fetchReturnRates();
  }, [withReturn, returnOnly, returnToId]);

  /** 수취인 주소 확인 — 캐리어 검증 + 이 주소에 매장(뚜레주르)이 등록되어 있는지 대조 */
  const checkAddress = async () => {
    if (!selectedStore) return;
    if (!toStreet.trim() || !toZip.trim()) { toast.error('주소(Street)와 ZIP 을 입력하세요'); return; }
    setCheckingAddr(true);
    setAddrCheck(null);
    try {
      const { data } = await api.post(`/store/shipping-address-check/${selectedStore._id}`, {
        toName, toStreet, toCity, toState, toZip,
      });
      setAddrCheck(data);
    } catch (e) {
      toast.error('주소 확인 실패: ' + (e.response?.data?.message || e.message));
    }
    setCheckingAddr(false);
  };

  // 다이얼로그 열릴 때 자동 요금 조회
  useEffect(() => {
    if (labelDialogOpen && selectedStore && !labelResult) {
      fetchRates();
    }
  }, [labelDialogOpen, selectedStore]);

  const createLabel = async () => {
    if (!selectedStore || (!returnOnly && selectedRate == null)) return;
    setCreatingLabel(true);
    try {
      const chosen = rates[selectedRate];
      const endpoint = returnOnly
        ? `/store/shipping-return-label/${selectedStore._id}`
        : withReturn
          ? `/store/shipping-with-return/${selectedStore._id}`
          : `/store/shipping-label/${selectedStore._id}`;
      // 리턴 반송 수신지: 주소록에서 선택한 주소 (없으면 발송지와 동일)
      const wantsReturn = withReturn || returnOnly;
      const returnTo = wantsReturn && returnToId ? addressBook.find(a => a.id === returnToId) : null;
      const returnChosen = wantsReturn && selectedReturnRate != null ? returnRates[selectedReturnRate] : null;
      const { data } = await api.post(endpoint, {
        ...(returnChosen ? { returnCarrier: returnChosen.carrier, returnService: returnChosen.service } : {}),
        carrier: chosen?.carrier,
        service: chosen?.service,
        weight: parcelWeight,
        length: parcelLength,
        width: parcelWidth,
        height: parcelHeight,
        toName, toStreet, toCity, toState, toZip, toPhone,
        fromCompany: shipFrom.company,
        fromStreet1: shipFrom.street1,
        fromCity: shipFrom.city,
        fromState: shipFrom.state,
        fromZip: shipFrom.zip,
        fromPhone: shipFrom.phone,
        ...(returnTo ? {
          returnCompany: returnTo.company,
          returnStreet1: returnTo.street1,
          returnCity: returnTo.city,
          returnState: returnTo.state,
          returnZip: returnTo.zip,
          returnPhone: returnTo.phone,
        } : {}),
      });
      setLabelResult(data);
      toast.success('라벨 생성 완료!');
      fetchStores();

      const pdfUrl = data.label?.labelUrl || data.outbound?.labelUrl || data.returnLabel?.labelUrl;
      if (pdfUrl) {
        window.open(pdfUrl, '_blank');
      }
    } catch (e) {
      toast.error('라벨 생성 실패: ' + (e.response?.data?.message || e.message));
    }
    setCreatingLabel(false);
  };

  const printLabel = (url) => {
    if (!url) return;
    const win = window.open('', '_blank');
    if (!win) return;
    const safe = String(url).replace(/&/g, '&amp;').replace(/"/g, '&quot;').replace(/</g, '&lt;');
    const path = String(url).split('?')[0].toLowerCase();
    const isPdf = path.endsWith('.pdf') || /label_file_type=pdf|format=pdf/i.test(String(url));

    if (isPdf) {
      // 구버전 PDF 라벨: Letter 페이지에 라벨이 작게 그려진 경우가 많음 — 4x6 용지 + 크기 맞춤 권장 안내
      win.document.write(`<!DOCTYPE html><html><head><meta charset="utf-8"/><title>Shipping Label</title>
        <style>
          @page { size: 4in 6in; margin: 0; }
          * { box-sizing: border-box; margin: 0; padding: 0; }
          html, body { width: 4in; height: 6in; background: #fff; display: flex; flex-direction: column; }
          embed { display: block; flex: 1; width: 3.8in; height: 5.6in; margin: 0.1in auto; }
          .hint { font: 11px/1.4 system-ui,sans-serif; padding: 6px 8px; color: #555; background: #fff8e1; }
          @media print { .hint { display: none !important; } }
        </style>
      </head><body>
        <div class="hint">인쇄 대화상자에서 용지 <strong>4×6 in</strong>, 배율 <strong>실제 크기(100%)</strong>를 선택하세요.</div>
        <embed src="${safe}" type="application/pdf" />
        <script>setTimeout(function(){ window.print(); }, 600);</script>
      </body></html>`);
      win.document.close();
      return;
    }

    win.document.write(`<!DOCTYPE html><html><head><meta charset="utf-8"/><title>Shipping Label</title>
      <style>
        @page { size: 4in 6in; margin: 0; }
        * { box-sizing: border-box; margin: 0; padding: 0; }
        html, body {
          width: 4in; height: 6in;
          background: #fff;
          display: flex;
          align-items: center;
          justify-content: center;
        }
        img {
          display: block;
          max-width: 3.8in;
          max-height: 5.8in;
          width: auto;
          height: auto;
          object-fit: contain;
        }
      </style>
    </head><body>
      <img src="${safe}" alt="Shipping label" onload="setTimeout(function(){ window.print(); }, 150)" />
    </body></html>`);
    win.document.close();
  };

  const isSentStore = (store) => ['label_created', 'shipped', 'delivered'].includes(store?.shippingStatus);
  const sentReferenceTime = (store) => {
    if (!isSentStore(store)) return 0;
    const ts = new Date(store?.lastShippedAt || store?.updatedAt || store?.createdAt || 0).getTime();
    return Number.isFinite(ts) ? ts : 0;
  };
  const searched = stores.filter(s =>
    (s.name || '').toLowerCase().includes(storeSearch.toLowerCase()) ||
    (s.ownerName || '').toLowerCase().includes(storeSearch.toLowerCase()) ||
    (s.town || '').toLowerCase().includes(storeSearch.toLowerCase()) ||
    (s.state || '').toLowerCase().includes(storeSearch.toLowerCase())
  );
  const filtered = searched.filter((s) => {
    if (shippingFilter === 'sent') return isSentStore(s);
    return true;
  });
  const visibleStores = [...filtered].sort((a, b) => {
    if (shippingSort !== 'recent-sent') return 0;
    return sentReferenceTime(b) - sentReferenceTime(a);
  });

  const labelUrl = labelResult?.label?.labelUrl || labelResult?.outbound?.labelUrl;
  const trackingCodeResult = labelResult?.label?.trackingCode || labelResult?.outbound?.trackingCode || labelResult?.returnLabel?.trackingCode;

  const weightLbs = (parcelWeight / 16).toFixed(1);

  return (
    <Box>
      {/* 헤더 */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2, flexWrap: 'wrap', gap: 1 }}>
        <Typography sx={{ fontWeight: 700, fontSize: 16 }}>
          <LocalShippingIcon sx={{ fontSize: 18, verticalAlign: 'middle', mr: 0.5 }} />
          배송 라벨 관리
        </Typography>
        <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', alignItems: 'center', maxWidth: { xs: '100%', sm: 420 } }}>
          <Tooltip title={`${shipFrom.company}, ${shipFrom.street1}, ${shipFrom.city}, ${shipFrom.state} ${shipFrom.zip} · ${shipFrom.phone}`}>
            <Chip
              label={`From: ${shipFrom.company} · ${shipFrom.city}, ${shipFrom.state}`}
              size="small"
              sx={{ fontSize: 10, maxWidth: 280, '& .MuiChip-label': { overflow: 'hidden', textOverflow: 'ellipsis' } }}
            />
          </Tooltip>
          <Chip label={`${parcelLength}×${parcelWidth}×${parcelHeight} in, ${weightLbs} lbs`} size="small" variant="outlined" sx={{ fontSize: 10 }} />
        </Box>
      </Box>

      {/* 검색 */}
      <Paper variant="outlined" sx={{ p: 1.5, borderRadius: 2, mb: 2 }}>
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
          <SearchIcon sx={{ color: '#888', fontSize: 20 }} />
          <TextField fullWidth size="small" placeholder="매장 이름, 지역, 주(State) 검색..."
            value={storeSearch} onChange={(e) => setStoreSearch(e.target.value)}
            sx={{ '& .MuiInputBase-root': { fontSize: 13 } }} />
          <Select
            size="small"
            value={shippingFilter}
            onChange={(e) => setShippingFilter(e.target.value)}
            sx={{ minWidth: 140, '& .MuiSelect-select': { fontSize: 12, py: 0.8 } }}
          >
            <MenuItem value="all" sx={{ fontSize: 12 }}>전체</MenuItem>
            <MenuItem value="sent" sx={{ fontSize: 12 }}>발송된 매장만</MenuItem>
          </Select>
          <Select
            size="small"
            value={shippingSort}
            onChange={(e) => setShippingSort(e.target.value)}
            sx={{ minWidth: 150, '& .MuiSelect-select': { fontSize: 12, py: 0.8 } }}
          >
            <MenuItem value="default" sx={{ fontSize: 12 }}>기본 정렬</MenuItem>
            <MenuItem value="recent-sent" sx={{ fontSize: 12 }}>최근 발송순</MenuItem>
          </Select>
          <Button size="small" startIcon={<RefreshIcon />} onClick={fetchStores}
            sx={{ whiteSpace: 'nowrap', fontSize: 11 }}>새로고침</Button>
        </Box>
        <Typography sx={{ fontSize: 11, color: '#888', mt: 0.5 }}>
          {visibleStores.length}개 매장 {storeSearch && `(검색: "${storeSearch}")`}
          {shippingSort === 'recent-sent' && ' · 최근 발송일 기준 정렬'}
        </Typography>
      </Paper>

      {/* Store 목록 */}
      {loadingStores ? (
        <Box sx={{ textAlign: 'center', py: 3 }}><CircularProgress size={24} /></Box>
      ) : (
        <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 2, maxHeight: 500 }}>
          <Table size="small" stickyHeader sx={{ tableLayout: 'fixed' }}>
            <TableHead>
              <TableRow sx={{ '& th': { fontWeight: 700, fontSize: 11, color: '#888', background: '#fafafa' } }}>
                <TableCell sx={{ width: 140 }}>Store</TableCell>
                <TableCell sx={{ width: 88 }}>Owner</TableCell>
                <TableCell sx={{ width: 240, maxWidth: 260 }}>주소</TableCell>
                <TableCell sx={{ width: 90 }}>배송 상태</TableCell>
                <TableCell sx={{ width: 140 }}>트래킹</TableCell>
                <TableCell sx={{ width: 220 }} align="center">액션</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {visibleStores.map((s) => (
                <TableRow key={s._id} hover sx={{
                  background: s.shippingStatus === 'delivered' ? 'rgba(34,197,94,0.04)' :
                    s.shippingStatus === 'label_created' ? 'rgba(21,101,192,0.04)' : 'white',
                }}>
                  <TableCell sx={{ fontSize: 12, fontWeight: 600, wordBreak: 'break-word' }}>
                    <Button
                      variant="text"
                      onClick={() => openHistoryDialog(s)}
                      sx={{
                        p: 0,
                        minWidth: 0,
                        fontSize: 12,
                        fontWeight: 700,
                        textTransform: 'none',
                        justifyContent: 'flex-start',
                        color: '#111',
                        textDecoration: 'underline',
                        textUnderlineOffset: '2px',
                        '&:hover': { background: 'transparent', color: '#551FFF' },
                      }}
                    >
                      {s.name}
                    </Button>
                  </TableCell>
                  <TableCell sx={{ fontSize: 11, color: '#555', wordBreak: 'break-word' }}>{s.ownerName || '-'}</TableCell>
                  <TableCell sx={{ maxWidth: 260, verticalAlign: 'middle' }}>
                    <Tooltip
                      title={<Typography component="span" sx={{ fontSize: 12, whiteSpace: 'pre-wrap' }}>{formatAddressTooltip(s)}</Typography>}
                      arrow
                      placement="top-start"
                    >
                      <Typography
                        component="div"
                        sx={{
                          fontSize: 12,
                          color: '#444',
                          lineHeight: 1.35,
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {[s.address1, s.town, s.state, s.zipcode].filter(Boolean).join(', ') || (
                          <span style={{ color: '#e53935' }}>주소 없음</span>
                        )}
                      </Typography>
                    </Tooltip>
                  </TableCell>
                  <TableCell>
                    {s.shippingStatus && s.shippingStatus !== 'pending' ? (
                      <Chip label={
                        s.shippingStatus === 'label_created' ? '라벨 생성됨' :
                        s.shippingStatus === 'shipped' ? '배송 중' :
                        s.shippingStatus === 'delivered' ? '배송 완료' : s.shippingStatus
                      } size="small"
                        color={s.shippingStatus === 'delivered' ? 'success' : s.shippingStatus === 'shipped' ? 'warning' : s.shippingStatus === 'label_created' ? 'info' : 'default'}
                        variant="outlined" sx={{ fontSize: 10, height: 20 }} />
                    ) : <Typography sx={{ fontSize: 11, color: '#ccc' }}>pending</Typography>}
                  </TableCell>
                  <TableCell sx={{ fontSize: 10, fontFamily: 'monospace', color: '#555', wordBreak: 'break-all' }}>
                    {s.shippingTrackingNumber || '—'}
                  </TableCell>
                  <TableCell align="center">
                    <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'center', flexWrap: 'wrap' }}>
                      {s.shippingLabelUrl ? (
                        <>
                          <Button size="small" variant="contained"
                            startIcon={<PrintIcon sx={{ fontSize: 14 }} />}
                            onClick={() => printLabel(s.shippingLabelUrl)}
                            sx={{ fontSize: 10, py: 0.3, px: 1, minWidth: 0, background: '#22c55e', '&:hover': { background: '#16a34a' } }}>
                            프린트
                          </Button>
                          <Button size="small" variant="outlined"
                            startIcon={<OpenInNewIcon sx={{ fontSize: 14 }} />}
                            href={s.shippingLabelUrl} target="_blank"
                            sx={{ fontSize: 10, py: 0.3, px: 1, minWidth: 0, borderColor: '#22c55e', color: '#22c55e' }}>
                            PDF
                          </Button>
                          <Button size="small" variant="outlined"
                            onClick={() => openLabelDialog(s)}
                            sx={{ fontSize: 10, py: 0.3, px: 1, minWidth: 0, borderColor: '#551FFF', color: '#551FFF' }}>
                            재생성
                          </Button>
                        </>
                      ) : (
                        <Button size="small" variant="contained"
                          startIcon={<PrintIcon sx={{ fontSize: 14 }} />}
                          onClick={() => openLabelDialog(s)}
                          disabled={!s.address1}
                          sx={{ fontSize: 10, py: 0.3, px: 1.5, background: '#551FFF', '&:hover': { background: '#4318e0' } }}>
                          라벨
                        </Button>
                      )}
                      {s.shippingTrackingNumber && (
                        <Button size="small" variant="outlined"
                          startIcon={<TrackChangesIcon sx={{ fontSize: 14 }} />}
                          onClick={async () => {
                            try {
                              const { data } = await api.get(`/store/shipping-tracking/${s._id}`);
                              setTrackingInfo(data);
                            } catch (_) { toast.error('트래킹 조회 실패'); }
                          }}
                          sx={{ fontSize: 10, py: 0.3, px: 1, minWidth: 0, borderColor: '#1565C0', color: '#1565C0' }}>
                          추적
                        </Button>
                      )}
                      <Button size="small" variant="text"
                        startIcon={<HistoryIcon sx={{ fontSize: 14 }} />}
                        onClick={() => openHistoryDialog(s)}
                        sx={{ fontSize: 10, py: 0.3, px: 1, minWidth: 0, color: '#888' }}>
                        이력
                      </Button>
                    </Box>
                  </TableCell>
                </TableRow>
              ))}
              {visibleStores.length === 0 && (
                <TableRow>
                  <TableCell colSpan={6} align="center" sx={{ py: 4, color: '#888', fontSize: 13 }}>
                    {stores.length === 0 ? '등록된 매장이 없습니다.' : '검색 결과 없음'}
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* ═══ 배송 라벨 생성 Dialog ═══ */}
      <Dialog open={labelDialogOpen} onClose={() => setLabelDialogOpen(false)} maxWidth="md" fullWidth
        PaperProps={{ sx: { borderRadius: 3 } }}>
        <DialogTitle sx={{ fontWeight: 700, fontSize: 16, borderBottom: '3px solid #551FFF', display: 'flex', alignItems: 'center', gap: 1 }}>
          <LocalShippingIcon sx={{ color: '#551FFF' }} />
          배송 라벨 — {selectedStore?.name}
        </DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          {/* 수취인 정보 (수정 가능) */}
          {selectedStore && (
            <Paper sx={{ p: 1.5, borderRadius: 2, mb: 2, mt: 1, background: '#f8f7ff', border: '1px solid #e5e7eb' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                <Typography sx={{ fontSize: 11, fontWeight: 700, color: '#551FFF' }}>TO (수신) — 수정 가능</Typography>
                <Button size="small" onClick={checkAddress} disabled={checkingAddr}
                  startIcon={checkingAddr ? <CircularProgress size={12} /> : <SearchIcon sx={{ fontSize: 14 }} />}
                  sx={{ fontSize: 10, color: '#551FFF' }}>
                  주소 확인 (등록 매장 대조)
                </Button>
              </Box>
              <Box sx={{ display: 'flex', gap: 1, mb: 1, alignItems: 'flex-start' }}>
                <TextField size="small" label="수취인 이름" value={toName}
                  onChange={(e) => setToName(e.target.value)} fullWidth
                  sx={{ '& input': { fontSize: 13, py: 0.7 }, '& label': { fontSize: 11 } }} />
                <TextField size="small" label="전화번호 (여러 개는 줄바꿈)" value={toPhone}
                  onChange={(e) => setToPhone(e.target.value)}
                  multiline minRows={1} maxRows={3}
                  helperText={splitPhones(toPhone).length > 1 ? '라벨에는 첫 번째 번호 사용' : ' '}
                  sx={{ width: 200, '& textarea': { fontSize: 13, lineHeight: 1.35 }, '& label': { fontSize: 11 },
                        '& .MuiFormHelperText-root': { fontSize: 9, mx: 0.5, mt: 0.2 } }} />
              </Box>
              {addrCheck && (
                <Alert
                  severity={addrCheck.matchStoreAddress || addrCheck.matchShippingAddress ? 'success' : (addrCheck.otherStoresAtAddress?.length ? 'warning' : 'info')}
                  onClose={() => setAddrCheck(null)}
                  sx={{ mb: 1, py: 0.3, '& .MuiAlert-message': { fontSize: 11, width: '100%' } }}>
                  {(addrCheck.matchStoreAddress || addrCheck.matchShippingAddress) ? (
                    <div>
                      <strong>{addrCheck.store?.name}</strong>{addrCheck.store?.tljNo ? ` (TLJ #${addrCheck.store.tljNo})` : ''} 이(가) 이 주소에 등록되어 있습니다
                      {addrCheck.matchShippingAddress && !addrCheck.matchStoreAddress ? ' (배송지 주소와 일치)' : ''}.
                    </div>
                  ) : (
                    <div>
                      입력 주소가 <strong>{addrCheck.store?.name}</strong> 의 등록 주소와 다릅니다.
                      {addrCheck.store?.address?.street1 && (
                        <span> 등록 주소: {addrCheck.store.address.street1}, {addrCheck.store.address.city}, {addrCheck.store.address.state} {addrCheck.store.address.zip}</span>
                      )}
                    </div>
                  )}
                  {addrCheck.otherStoresAtAddress?.length > 0 && (
                    <div style={{ marginTop: 4 }}>
                      같은 주소에 등록된 다른 매장: {addrCheck.otherStoresAtAddress.map(o => `${o.name}${o.tljNo ? ` (TLJ #${o.tljNo})` : ''}`).join(', ')}
                    </div>
                  )}
                  {addrCheck.validation && (
                    <div style={{ marginTop: 4, color: addrCheck.validation.isValid === false ? '#c62828' : '#555' }}>
                      캐리어 주소 검증: {addrCheck.validation.isValid === false ? '실패' : addrCheck.validation.isValid === null ? '확인 불가' : '통과'}
                      {addrCheck.validation.normalized?.street1 && addrCheck.validation.isValid !== false
                        ? ` — ${addrCheck.validation.normalized.street1}, ${addrCheck.validation.normalized.city}, ${addrCheck.validation.normalized.state} ${addrCheck.validation.normalized.zip}` : ''}
                      {addrCheck.validation.messages?.length ? ` (${addrCheck.validation.messages.join('; ')})` : ''}
                    </div>
                  )}
                </Alert>
              )}
              <Box sx={{ mb: 1 }}>
                <TextField size="small" label="주소 (Street)" value={toStreet}
                  onChange={(e) => setToStreet(e.target.value)} fullWidth
                  sx={{ '& input': { fontSize: 13, py: 0.7 }, '& label': { fontSize: 11 } }} />
              </Box>
              <Box sx={{ display: 'flex', gap: 1, mb: 1.5 }}>
                <TextField size="small" label="City" value={toCity}
                  onChange={(e) => setToCity(e.target.value)} fullWidth
                  sx={{ '& input': { fontSize: 13, py: 0.7 }, '& label': { fontSize: 11 } }} />
                <TextField size="small" label="State" value={toState}
                  onChange={(e) => setToState(e.target.value)}
                  sx={{ width: 80, '& input': { fontSize: 13, py: 0.7 }, '& label': { fontSize: 11 } }} />
                <TextField size="small" label="ZIP" value={toZip}
                  onChange={(e) => setToZip(e.target.value)}
                  sx={{ width: 100, '& input': { fontSize: 13, py: 0.7 }, '& label': { fontSize: 11 } }} />
              </Box>

              <Divider sx={{ my: 1.5 }} />
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                <Typography sx={{ fontSize: 11, fontWeight: 700, color: '#888' }}>
                  <BookmarkIcon sx={{ fontSize: 13, verticalAlign: 'middle', mr: 0.3 }} />
                  FROM (발송) — 주소록
                </Typography>
                <Button size="small" startIcon={<BookmarkAddIcon sx={{ fontSize: 14 }} />}
                  onClick={() => { setEditingAddress(null); setAddressDialogOpen(true); }}
                  sx={{ fontSize: 10, color: '#551FFF' }}>
                  새 주소 추가
                </Button>
              </Box>
              <Select
                size="small" fullWidth
                value={selectedFromId}
                onChange={(e) => { selectFromAddress(e.target.value); }}
                sx={{ fontSize: 12, mb: 1, '& .MuiSelect-select': { py: 1 } }}
              >
                {addressBook.map((a) => (
                  <MenuItem key={a.id} value={a.id} sx={{ fontSize: 12 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', width: '100%', alignItems: 'center' }}>
                      <Box>
                        <strong>{a.label || a.company}</strong>
                        <Typography component="span" sx={{ fontSize: 10, color: '#888', ml: 1 }}>
                          {a.street1}, {a.city}, {a.state} {a.zip}
                        </Typography>
                      </Box>
                    </Box>
                  </MenuItem>
                ))}
              </Select>
              {/* 선택된 주소 표시 + 수정/삭제 */}
              <Paper variant="outlined" sx={{ p: 1.2, borderRadius: 1.5, background: '#fafafa' }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                  <Box>
                    <Typography sx={{ fontSize: 12, fontWeight: 700 }}>{shipFrom.company}</Typography>
                    <Typography sx={{ fontSize: 11, color: '#555' }}>{shipFrom.street1}</Typography>
                    <Typography sx={{ fontSize: 11, color: '#555' }}>{shipFrom.city}, {shipFrom.state} {shipFrom.zip}</Typography>
                    {splitPhones(shipFrom.phone).map((p, i) => (
                      <Typography key={i} sx={{ fontSize: 10, color: '#888' }}>Tel: {p}</Typography>
                    ))}
                  </Box>
                  <Box sx={{ display: 'flex', gap: 0.3 }}>
                    <IconButton size="small" onClick={() => { setEditingAddress({ ...shipFrom }); setAddressDialogOpen(true); }}
                      sx={{ color: '#551FFF' }} title="수정">
                      <EditIcon sx={{ fontSize: 16 }} />
                    </IconButton>
                    <IconButton size="small" onClick={() => deleteAddress(shipFrom.id)}
                      sx={{ color: '#e53935' }} title="삭제">
                      <DeleteOutlineIcon sx={{ fontSize: 16 }} />
                    </IconButton>
                  </Box>
                </Box>
              </Paper>
              <Typography sx={{ fontSize: 10, color: '#999', mt: 0.75 }}>
                발송지를 변경한 뒤에는 아래 「무게/크기 변경 후 요금 다시 조회」를 눌러 요금을 갱신하세요.
              </Typography>
            </Paper>
          )}

          {/* 패키지 크기/무게 설정 */}
          <Paper variant="outlined" sx={{ p: 1.5, borderRadius: 2, mb: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
              <ScaleIcon sx={{ fontSize: 16, color: '#551FFF' }} />
              <Typography sx={{ fontSize: 12, fontWeight: 700 }}>패키지 크기 / 무게</Typography>
            </Box>
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
              <TextField size="small" label="길이(in)" type="number" value={parcelLength}
                onChange={(e) => setParcelLength(Number(e.target.value))}
                sx={{ width: 80, '& input': { fontSize: 13, py: 0.8 }, '& label': { fontSize: 11 } }}
                inputProps={{ min: 1, max: 50 }} />
              <Typography sx={{ fontSize: 12, color: '#888' }}>×</Typography>
              <TextField size="small" label="너비(in)" type="number" value={parcelWidth}
                onChange={(e) => setParcelWidth(Number(e.target.value))}
                sx={{ width: 80, '& input': { fontSize: 13, py: 0.8 }, '& label': { fontSize: 11 } }}
                inputProps={{ min: 1, max: 50 }} />
              <Typography sx={{ fontSize: 12, color: '#888' }}>×</Typography>
              <TextField size="small" label="높이(in)" type="number" value={parcelHeight}
                onChange={(e) => setParcelHeight(Number(e.target.value))}
                sx={{ width: 80, '& input': { fontSize: 13, py: 0.8 }, '& label': { fontSize: 11 } }}
                inputProps={{ min: 1, max: 50 }} />
              <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />
              <TextField size="small" label="무게(oz)" type="number" value={parcelWeight}
                onChange={(e) => setParcelWeight(Number(e.target.value))}
                sx={{ width: 90, '& input': { fontSize: 13, py: 0.8, fontWeight: 700 }, '& label': { fontSize: 11 } }}
                inputProps={{ min: 1, max: 1200 }} />
              <Typography sx={{ fontSize: 11, color: '#888', whiteSpace: 'nowrap' }}>
                = {(parcelWeight / 16).toFixed(1)} lbs
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', gap: 0.5, mt: 1, flexWrap: 'wrap' }}>
              {[
                { label: 'Bubble Mailer (1.5lb)', l: 13, w: 10, h: 2, wt: 24 },
                { label: 'Small Box (3lb)', l: 12, w: 10, h: 6, wt: 48 },
                { label: 'Medium Box (5lb)', l: 16, w: 12, h: 8, wt: 80 },
                { label: 'Large Box (10lb)', l: 20, w: 16, h: 12, wt: 160 },
              ].map((preset) => (
                <Chip key={preset.label} label={preset.label} size="small" variant="outlined"
                  onClick={() => {
                    setParcelLength(preset.l); setParcelWidth(preset.w);
                    setParcelHeight(preset.h); setParcelWeight(preset.wt);
                  }}
                  sx={{
                    fontSize: 10, cursor: 'pointer',
                    borderColor: parcelWeight === preset.wt ? '#551FFF' : undefined,
                    color: parcelWeight === preset.wt ? '#551FFF' : undefined,
                    fontWeight: parcelWeight === preset.wt ? 700 : 400,
                  }} />
              ))}
            </Box>
            {!rates.length && !loadingRates && (
              <Button size="small" variant="contained" onClick={fetchRates}
                sx={{ mt: 1, fontSize: 11, background: '#551FFF', '&:hover': { background: '#4318e0' } }}>
                요금 조회
              </Button>
            )}
          </Paper>

          {/* 요금 비교 — 라디오 선택 */}
          {(loadingRates || rates.length > 0) && (
            <Typography sx={{ fontSize: 13, fontWeight: 700, mb: 0.5 }}>캐리어별 요금 비교</Typography>
          )}
          {loadingRates ? (
            <Box sx={{ textAlign: 'center', py: 3 }}><CircularProgress size={28} /></Box>
          ) : rates.length > 0 ? (
            <>
              <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 2, mb: 2, maxHeight: 300 }}>
                <Table size="small" stickyHeader>
                  <TableHead>
                    <TableRow sx={{ '& th': { fontSize: 11, fontWeight: 700, color: '#888', background: '#fafafa' } }}>
                      <TableCell width={40}></TableCell>
                      <TableCell>캐리어</TableCell>
                      <TableCell>서비스</TableCell>
                      <TableCell align="right">요금</TableCell>
                      <TableCell align="right">배송일</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {rates.map((r, i) => (
                      <TableRow key={i} hover onClick={() => setSelectedRate(i)}
                        sx={{ cursor: 'pointer', background: selectedRate === i ? 'rgba(85,31,255,0.06)' : i === 0 ? 'rgba(34,197,94,0.04)' : 'white' }}>
                        <TableCell><Radio size="small" checked={selectedRate === i} sx={{ p: 0.3 }} /></TableCell>
                        <TableCell sx={{ fontSize: 12, fontWeight: selectedRate === i ? 700 : 400 }}>{r.carrier}</TableCell>
                        <TableCell sx={{ fontSize: 12 }}>{r.service}</TableCell>
                        <TableCell align="right" sx={{ fontSize: 13, fontWeight: 700, color: i === 0 ? '#22c55e' : selectedRate === i ? '#551FFF' : '#333' }}>
                          ${r.price}
                        </TableCell>
                        <TableCell align="right" sx={{ fontSize: 12 }}>
                          {r.deliveryDays ? `${r.deliveryDays}일` : '-'}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
              <Button size="small" variant="text" onClick={fetchRates}
                sx={{ fontSize: 10, color: '#888', mb: 1 }}>
                무게/크기 변경 후 요금 다시 조회
              </Button>
            </>
          ) : null}

          {/* 리턴 라벨 옵션 */}
          {!labelResult && (
            <Box sx={{ mb: 1, p: 1, background: '#fafafa', borderRadius: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <input type="checkbox" checked={withReturn} disabled={returnOnly}
                  onChange={(e) => setWithReturn(e.target.checked)}
                  style={{ accentColor: '#551FFF', width: 16, height: 16 }} />
                <Typography sx={{ fontSize: 12, color: returnOnly ? '#aaa' : 'inherit' }}>리턴 라벨도 함께 생성 (반품용)</Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
                <input type="checkbox" checked={returnOnly}
                  onChange={(e) => { setReturnOnly(e.target.checked); if (e.target.checked) setWithReturn(false); }}
                  style={{ accentColor: '#551FFF', width: 16, height: 16 }} />
                <Typography sx={{ fontSize: 12 }}>리턴 라벨만 생성 (아웃바운드 없이 — 매장 → 반송 수신지)</Typography>
              </Box>
              {(withReturn || returnOnly) && (
                <Box sx={{ mt: 1, pl: 3 }}>
                  <Typography sx={{ fontSize: 10, color: '#888', mb: 0.5 }}>리턴 수신지 (반품을 받을 주소) — 발송지와 다른 주소로 받을 수 있습니다</Typography>
                  <Select size="small" fullWidth value={returnToId} displayEmpty
                    onChange={(e) => setReturnToId(e.target.value)}
                    sx={{ fontSize: 12, '& .MuiSelect-select': { py: 0.8 } }}>
                    <MenuItem value="" sx={{ fontSize: 12 }}>
                      발송지와 동일 — {shipFrom.label || shipFrom.company} ({shipFrom.street1}, {shipFrom.city}, {shipFrom.state} {shipFrom.zip})
                    </MenuItem>
                    {addressBook.filter(a => a.id !== shipFrom.id).map((a) => (
                      <MenuItem key={a.id} value={a.id} sx={{ fontSize: 12 }}>
                        <strong>{a.label || a.company}</strong>
                        <Typography component="span" sx={{ fontSize: 10, color: '#888', ml: 1 }}>
                          {a.street1}, {a.city}, {a.state} {a.zip}
                        </Typography>
                      </MenuItem>
                    ))}
                  </Select>
                  <Typography sx={{ fontSize: 10, color: '#999', mt: 0.5 }}>
                    목록에 없는 주소는 위 「새 주소 추가」로 주소록에 먼저 등록하세요.
                  </Typography>

                  {/* 리턴 배송 방법 — 보내는 건 급하지만 받는 건 천천히 받아도 되므로 별도 선택 (기본 최저가) */}
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1, mb: 0.5 }}>
                    <Typography sx={{ fontSize: 10, color: '#888' }}>리턴 배송 방법 (기본: 최저가)</Typography>
                    <Button size="small" onClick={fetchReturnRates} disabled={loadingReturnRates}
                      startIcon={loadingReturnRates ? <CircularProgress size={10} /> : <RefreshIcon sx={{ fontSize: 12 }} />}
                      sx={{ fontSize: 9, color: '#551FFF', py: 0, minHeight: 0 }}>
                      다시 조회
                    </Button>
                  </Box>
                  {returnRates.length > 0 ? (
                    <Select size="small" fullWidth value={selectedReturnRate ?? ''}
                      onChange={(e) => setSelectedReturnRate(Number(e.target.value))}
                      sx={{ fontSize: 12, '& .MuiSelect-select': { py: 0.8 } }}>
                      {returnRates.map((r, i) => (
                        <MenuItem key={r.id || i} value={i} sx={{ fontSize: 12 }}>
                          {r.carrier} {r.service} — ${r.price}{r.deliveryDays ? ` (${r.deliveryDays}일)` : ''}{i === 0 ? ' · 최저가' : ''}
                        </MenuItem>
                      ))}
                    </Select>
                  ) : (
                    <Typography sx={{ fontSize: 10, color: '#999' }}>
                      {loadingReturnRates ? '리턴 요금 조회 중...' : '리턴 요금 없음 — 최저가로 자동 생성됩니다'}
                    </Typography>
                  )}
                </Box>
              )}
            </Box>
          )}

          {/* 라벨 생성 결과 */}
          {labelResult && (
            <Alert severity="success" sx={{ mb: 1, '& .MuiAlert-message': { width: '100%' } }}>
              <Typography sx={{ fontSize: 14, fontWeight: 700, mb: 1 }}>라벨 생성 완료!</Typography>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                <Box>
                  <Typography sx={{ fontSize: 12 }}>Tracking: <strong style={{ fontFamily: 'monospace' }}>{trackingCodeResult || '-'}</strong></Typography>
                  <Typography sx={{ fontSize: 11, color: '#888' }}>
                    {labelResult.label?.rate?.carrier || labelResult.outbound?.rate?.carrier || labelResult.returnLabel?.rate?.carrier} — ${labelResult.label?.rate?.price || labelResult.outbound?.rate?.price || labelResult.returnLabel?.rate?.price}{labelResult.returnLabel && !labelResult.outbound && !labelResult.label ? ' (리턴 라벨)' : ''}
                  </Typography>
                </Box>
              </Box>
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                {labelUrl && (
                  <>
                    <Button size="small" variant="contained" startIcon={<PrintIcon />}
                      onClick={() => printLabel(labelUrl)}
                      sx={{ fontSize: 11, background: '#551FFF', '&:hover': { background: '#4318e0' } }}>
                      라벨 프린트
                    </Button>
                    <Button size="small" variant="outlined" startIcon={<OpenInNewIcon />}
                      href={labelUrl} target="_blank"
                      sx={{ fontSize: 11 }}>
                      PDF 보기
                    </Button>
                  </>
                )}
                {labelResult.returnLabel?.labelUrl && (
                  <>
                    <Button size="small" variant="outlined" startIcon={<PrintIcon />}
                      onClick={() => printLabel(labelResult.returnLabel.labelUrl)}
                      sx={{ fontSize: 11, borderColor: '#FF6A00', color: '#FF6A00' }}>
                      리턴 라벨 프린트
                    </Button>
                    <Button size="small" variant="text" startIcon={<OpenInNewIcon />}
                      href={labelResult.returnLabel.labelUrl} target="_blank"
                      sx={{ fontSize: 11 }}>
                      리턴 PDF
                    </Button>
                  </>
                )}
              </Box>
            </Alert>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
          <Button onClick={() => setLabelDialogOpen(false)} sx={{ color: '#888' }}>닫기</Button>
          {!labelResult && (
            <Button variant="contained" onClick={createLabel}
              disabled={creatingLabel || (returnOnly ? returnRates.length === 0 : (rates.length === 0 || selectedRate === null))}
              startIcon={creatingLabel ? <CircularProgress size={16} /> : <PrintIcon />}
              sx={{ fontSize: 13, fontWeight: 700, px: 3, background: '#551FFF', '&:hover': { background: '#4318e0' } }}>
              {creatingLabel ? '생성 중...' : returnOnly
                ? `리턴 라벨 생성 ($${returnRates[selectedReturnRate ?? 0]?.price || '최저가'})`
                : `라벨 생성 ($${rates[selectedRate]?.price || ''})${withReturn ? ` + 리턴 ($${returnRates[selectedReturnRate ?? 0]?.price || '최저가'})` : ''}`}
            </Button>
          )}
        </DialogActions>
      </Dialog>

      {/* ═══ 트래킹 Dialog ═══ */}
      <Dialog open={!!trackingInfo} onClose={() => setTrackingInfo(null)} maxWidth="sm" fullWidth
        PaperProps={{ sx: { borderRadius: 3 } }}>
        <DialogTitle sx={{ fontWeight: 700, fontSize: 16, borderBottom: '3px solid #1565C0', display: 'flex', alignItems: 'center', gap: 1 }}>
          <TrackChangesIcon sx={{ color: '#1565C0' }} />
          배송 추적 — {trackingInfo?.store?.name}
        </DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          {trackingInfo && (
            <Box>
              <Paper sx={{ p: 1.5, borderRadius: 2, mb: 2, mt: 1, background: '#f0f7ff' }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                  <Typography sx={{ fontSize: 12, color: '#888' }}>Tracking #</Typography>
                  <Typography sx={{ fontSize: 12, fontWeight: 700, fontFamily: 'monospace' }}>
                    {trackingInfo.store?.trackingNumber || trackingInfo.trackingNumber || '-'}
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                  <Typography sx={{ fontSize: 12, color: '#888' }}>Carrier</Typography>
                  <Typography sx={{ fontSize: 12, fontWeight: 600 }}>
                    {trackingInfo.store?.carrier || trackingInfo.carrier || '-'}
                  </Typography>
                </Box>
                {trackingInfo.tracking && (
                  <>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                      <Typography sx={{ fontSize: 12, color: '#888' }}>Status</Typography>
                      <Chip label={trackingInfo.tracking.status} size="small"
                        color={trackingInfo.tracking.status === 'delivered' ? 'success' : trackingInfo.tracking.status === 'in_transit' ? 'info' : 'default'}
                        sx={{ fontSize: 10, height: 20, fontWeight: 700 }} />
                    </Box>
                    {trackingInfo.tracking.estDelivery && (
                      <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                        <Typography sx={{ fontSize: 12, color: '#888' }}>예상 도착</Typography>
                        <Typography sx={{ fontSize: 12, fontWeight: 600 }}>{new Date(trackingInfo.tracking.estDelivery).toLocaleDateString('ko-KR')}</Typography>
                      </Box>
                    )}
                  </>
                )}
              </Paper>

              {trackingInfo.tracking?.events?.length > 0 && (
                <Box>
                  <Typography sx={{ fontSize: 12, fontWeight: 700, mb: 1 }}>배송 이력</Typography>
                  {trackingInfo.tracking.events.map((evt, i) => (
                    <Box key={i} sx={{ display: 'flex', gap: 1.5, py: 0.8, borderLeft: `2px solid ${i === 0 ? '#1565C0' : '#e5e7eb'}`, pl: 1.5, ml: 0.5 }}>
                      <Box sx={{ width: 8, height: 8, borderRadius: '50%', background: i === 0 ? '#1565C0' : '#ddd', mt: 0.3, flexShrink: 0, ml: -1.8 }} />
                      <Box>
                        <Typography sx={{ fontSize: 12, fontWeight: i === 0 ? 600 : 400 }}>{evt.message}</Typography>
                        <Typography sx={{ fontSize: 10, color: '#888' }}>
                          {evt.city && `${evt.city}, ${evt.state} · `}{new Date(evt.datetime).toLocaleString('ko-KR')}
                        </Typography>
                      </Box>
                    </Box>
                  ))}
                </Box>
              )}

              {trackingInfo.message && !trackingInfo.tracking && (
                <Alert severity="info" sx={{ fontSize: 12 }}>{trackingInfo.message}</Alert>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setTrackingInfo(null)}>닫기</Button>
        </DialogActions>
      </Dialog>

      {/* ═══ 라벨 이력 Dialog ═══ */}
      <Dialog open={historyDialogOpen} onClose={() => setHistoryDialogOpen(false)} maxWidth="md" fullWidth
        PaperProps={{ sx: { borderRadius: 3 } }}>
        <DialogTitle sx={{ fontWeight: 700, fontSize: 16, borderBottom: '3px solid #551FFF', display: 'flex', alignItems: 'center', gap: 1 }}>
          <HistoryIcon sx={{ color: '#551FFF' }} />
          라벨 생성 이력 — {historyStore?.name}
        </DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          {labelHistory.length === 0 ? (
            <Typography sx={{ py: 4, textAlign: 'center', color: '#888', fontSize: 13 }}>
              라벨 생성 이력이 없습니다.
            </Typography>
          ) : (
            <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 2, mt: 1 }}>
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ '& th': { fontSize: 11, fontWeight: 700, color: '#888', background: '#fafafa' } }}>
                    <TableCell>생성일</TableCell>
                    <TableCell>캐리어</TableCell>
                    <TableCell>서비스</TableCell>
                    <TableCell align="right">요금</TableCell>
                    <TableCell>트래킹</TableCell>
                    <TableCell>무게</TableCell>
                    <TableCell>수신지</TableCell>
                    <TableCell>상태 Status</TableCell>
                    <TableCell align="center">라벨</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {labelHistory.map((h) => (
                    <TableRow key={h._id} hover>
                      <TableCell sx={{ fontSize: 11, whiteSpace: 'nowrap' }}>
                        {new Date(h.createdAt).toLocaleString('ko-KR', { month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit' })}
                      </TableCell>
                      <TableCell sx={{ fontSize: 12, fontWeight: 600 }}>{h.carrier}</TableCell>
                      <TableCell sx={{ fontSize: 11 }}>{h.service}</TableCell>
                      <TableCell align="right" sx={{ fontSize: 12, fontWeight: 700, color: '#551FFF' }}>${h.price}</TableCell>
                      <TableCell sx={{ fontSize: 10, fontFamily: 'monospace', wordBreak: 'break-all' }}>{h.trackingCode}</TableCell>
                      <TableCell sx={{ fontSize: 11 }}>{h.parcel?.weight ? `${(h.parcel.weight / 16).toFixed(1)}lb` : '-'}</TableCell>
                      <TableCell sx={{ fontSize: 11 }}>
                        {h.toAddress ? `${h.toAddress.city}, ${h.toAddress.state}` : '-'}
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={h.status === 'voided' ? 'Voided 취소됨' : h.status === 'shipped' ? 'Shipped 배송중' : h.status === 'delivered' ? 'Delivered 배송완료' : 'Created 생성됨'}
                          size="small"
                          color={h.status === 'voided' ? 'error' : h.status === 'delivered' ? 'success' : 'default'}
                          variant="outlined"
                          sx={{ fontSize: 9, height: 20 }}
                        />
                      </TableCell>
                      <TableCell align="center">
                        {h.status === 'voided' ? (
                          <Typography sx={{ fontSize: 10, color: '#e53935' }}>Voided 취소됨</Typography>
                        ) : h.labelUrl ? (
                          <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'center', flexWrap: 'wrap' }}>
                            <Button size="small" variant="contained"
                              onClick={() => printLabel(h.labelUrl)}
                              sx={{ fontSize: 9, py: 0.2, px: 0.8, minWidth: 0, background: '#22c55e', '&:hover': { background: '#16a34a' } }}>
                              프린트
                            </Button>
                            <Button size="small" variant="outlined"
                              href={h.labelUrl} target="_blank"
                              sx={{ fontSize: 9, py: 0.2, px: 0.8, minWidth: 0, borderColor: '#22c55e', color: '#22c55e' }}>
                              PDF
                            </Button>
                            <Button size="small" variant="outlined"
                              startIcon={<BlockIcon sx={{ fontSize: 12 }} />}
                              onClick={() => voidLabel(h._id, h.store)}
                              sx={{ fontSize: 9, py: 0.2, px: 0.8, minWidth: 0, borderColor: '#e53935', color: '#e53935' }}>
                              Void 취소
                            </Button>
                          </Box>
                        ) : <Typography sx={{ fontSize: 10, color: '#ccc' }}>—</Typography>}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setHistoryDialogOpen(false)} sx={{ color: '#888' }}>닫기</Button>
        </DialogActions>
      </Dialog>

      {/* ═══ 주소록 편집 Dialog ═══ */}
      <AddressEditDialog
        open={addressDialogOpen}
        address={editingAddress}
        onClose={() => { setAddressDialogOpen(false); setEditingAddress(null); }}
        onSave={saveAddress}
      />
    </Box>
  );
}

/** 주소록 추가/수정 Dialog */
function AddressEditDialog({ open, address, onClose, onSave }) {
  const [form, setForm] = useState({ label: '', company: '', street1: '', city: '', state: '', zip: '', phone: '' });

  useEffect(() => {
    if (open) {
      setForm(address
        ? { ...address }
        : { label: '', company: '', street1: '', city: '', state: '', zip: '', phone: '' }
      );
    }
  }, [open, address]);

  const update = (key, val) => setForm(f => ({ ...f, [key]: val }));

  const handleSave = () => {
    if (!form.street1?.trim() || !form.city?.trim() || !form.state?.trim() || !form.zip?.trim()) {
      toast.error('주소 필수 항목을 입력하세요');
      return;
    }
    if (!form.label?.trim()) {
      form.label = `${form.company || 'Custom'} (${form.city})`;
    }
    onSave({ ...form, id: address?.id || undefined });
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
      <DialogTitle sx={{ fontWeight: 700, fontSize: 16, borderBottom: '3px solid #551FFF' }}>
        <BookmarkIcon sx={{ fontSize: 18, verticalAlign: 'middle', mr: 0.5, color: '#551FFF' }} />
        {address ? '발송지 수정' : '새 발송지 추가'}
      </DialogTitle>
      <DialogContent sx={{ pt: 2 }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, mt: 1 }}>
          <TextField size="small" label="이름 (구분용)" value={form.label} placeholder="예: Digntec HQ, 뉴저지 창고"
            onChange={(e) => update('label', e.target.value)} fullWidth
            sx={{ '& input': { fontSize: 13 }, '& label': { fontSize: 12 } }} />
          <Box sx={{ display: 'flex', gap: 1 }}>
            <TextField size="small" label="회사명 *" value={form.company}
              onChange={(e) => update('company', e.target.value)} fullWidth
              sx={{ '& input': { fontSize: 13 }, '& label': { fontSize: 12 } }} />
            <TextField size="small" label="전화번호 (여러 개는 줄바꿈)" value={form.phone}
              onChange={(e) => update('phone', e.target.value)}
              multiline minRows={1} maxRows={3}
              sx={{ width: 200, '& textarea': { fontSize: 13, lineHeight: 1.35 }, '& label': { fontSize: 12 } }} />
          </Box>
          <TextField size="small" label="Street *" value={form.street1}
            onChange={(e) => update('street1', e.target.value)} fullWidth
            sx={{ '& input': { fontSize: 13 }, '& label': { fontSize: 12 } }} />
          <Box sx={{ display: 'flex', gap: 1 }}>
            <TextField size="small" label="City *" value={form.city}
              onChange={(e) => update('city', e.target.value)} fullWidth
              sx={{ '& input': { fontSize: 13 }, '& label': { fontSize: 12 } }} />
            <TextField size="small" label="State *" value={form.state}
              onChange={(e) => update('state', e.target.value)}
              sx={{ width: 80, '& input': { fontSize: 13 }, '& label': { fontSize: 12 } }} />
            <TextField size="small" label="ZIP *" value={form.zip}
              onChange={(e) => update('zip', e.target.value)}
              sx={{ width: 110, '& input': { fontSize: 13 }, '& label': { fontSize: 12 } }} />
          </Box>
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
        <Button onClick={onClose} sx={{ color: '#888' }}>취소</Button>
        <Button variant="contained" onClick={handleSave}
          sx={{ background: '#551FFF', '&:hover': { background: '#4318e0' }, fontWeight: 700, px: 3 }}>
          {address ? '수정' : '추가'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
